import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { MysteryType, ChapletType, NovenaPhase, PrayerType, MoodType } from '../../types';
import { getOpeningPrayer, getClosingPrayer, getDecadePrayers } from '../../utils/prayers';
import { ROSARY_MYSTERIES, MYSTERY_REFLECTIONS } from '../../constants/novena';
import { CHAPLET_INFO, CHAPLET_PRAYERS } from '../../constants/chaplets';
import { ExpandablePrayer } from '../common/ExpandablePrayer';
import { getPrayerText } from '../../constants/commonPrayers';
import { VirtualRosaryDrawer } from '../prayer/VirtualRosaryDrawer';
import { RosaryToggleButton } from '../prayer/RosaryToggleButton';
import { RosaryFooterCounter } from '../prayer/RosaryFooterCounter';
import { BeadSequence } from '../../constants/rosarySequences';
import { getBeadIndexForStep, getStepIndexForBead, getDecadeForBead, isOurFatherBead, OurFatherPhase } from '../../utils/rosaryMapping';
import {
  requestWakeLock,
  releaseWakeLock,
  getKeepScreenAwakePreference
} from '../../utils/screenWakeLock';
import { audioFeedback, useAudioPreference } from '../../utils/audioFeedback';
import { PrePrayerIntention } from '../journal/PrePrayerIntention';
import { PostPrayerReflection } from '../journal/PostPrayerReflection';

interface PrayerModalProps {
  isOpen: boolean;
  prayerType: PrayerType;
  currentDay?: number; // Optional for daily rosary
  mystery?: MysteryType; // Optional for chaplets
  chaplet?: ChapletType; // Optional for chaplets
  phase?: NovenaPhase; // Optional for daily rosary
  intention?: string;
  onClose: () => void;
  onComplete: (journalData?: {
    intention?: string;
    reflection?: string;
    mood?: MoodType;
    gratitudes?: string[];
    insights?: string;
    tags?: string[];
  }) => void;
}

interface PrayerStep {
  id: string;
  title: string;
  content: string | string[];
  type: 'prayer' | 'instruction' | 'mysteries' | 'decades' | 'intention' | 'reflection';
}

export const PrayerModal: React.FC<PrayerModalProps> = ({
  isOpen,
  prayerType,
  currentDay = 1,
  mystery,
  chaplet,
  phase = 'petition',
  intention = '',
  onClose,
  onComplete
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showRosaryDrawer, setShowRosaryDrawer] = useState(false);
  const [currentBeadIndex, setCurrentBeadIndex] = useState(0);
  // Track which phase we're on for Our Father beads (closing prayers vs opening prayers)
  const [ourFatherPhase, setOurFatherPhase] = useState<OurFatherPhase>('opening');
  const audioEnabled = useAudioPreference();
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showResetToast, setShowResetToast] = useState(false);

  // Journal state
  const [journalData, setJournalData] = useState<{
    intention?: string;
    reflection?: string;
    mood?: MoodType;
    gratitudes?: string[];
    insights?: string;
    tags?: string[];
  }>({
    intention: intention || undefined
  });

  // Generate unique key for this prayer session
  const prayerSessionKey = `prayer_progress_${prayerType}_${chaplet || mystery || 'default'}_${currentDay || 0}`;

  // Load saved progress when modal opens
  useEffect(() => {
    if (isOpen) {
      const savedProgress = localStorage.getItem(prayerSessionKey);
      if (savedProgress) {
        try {
          const { stepIndex, beadIndex } = JSON.parse(savedProgress);
          setCurrentStepIndex(stepIndex);
          setCurrentBeadIndex(beadIndex);
        } catch (error) {
          console.warn('Failed to parse saved prayer progress:', error);
        }
      }
    }
  }, [isOpen, prayerSessionKey]);

  // Save progress whenever step changes
  useEffect(() => {
    if (isOpen && currentStepIndex > 0) {
      const progressData = {
        stepIndex: currentStepIndex,
        beadIndex: currentBeadIndex,
        timestamp: Date.now()
      };
      localStorage.setItem(prayerSessionKey, JSON.stringify(progressData));
    }
  }, [currentStepIndex, currentBeadIndex, isOpen, prayerSessionKey]);

  // Handle wake lock when modal opens/closes
  useEffect(() => {
    if (isOpen && getKeepScreenAwakePreference()) {
      requestWakeLock();
    }

    return () => {
      // Always release wake lock when modal closes or component unmounts
      releaseWakeLock();
    };
  }, [isOpen]);

  // Track whether the step change came from bead navigation (to avoid resetting bead position)
  const stepChangeFromBead = React.useRef(false);

  // Sync bead position when step changes (user clicks Next/Previous)
  // This ensures the bead jumps to the correct position (e.g., Our Father bead at start of decade)
  useEffect(() => {
    // Only sync bead position if the step change came from navigation (Next/Previous buttons)
    // not from bead swiping
    if (!stepChangeFromBead.current) {
      const newBeadIndex = getBeadIndexForStep(currentStepIndex, prayerType, chaplet);
      setCurrentBeadIndex(newBeadIndex);
    }
    // Reset the flag for the next change
    stepChangeFromBead.current = false;
  }, [currentStepIndex, prayerType, chaplet]);

  if (!isOpen) return null;

  const isNovena = prayerType === '54-day-novena';
  const isChaplet = prayerType === 'chaplet';

  // Pilot gate: bead-by-bead drawer supports Rosary, 54-Day Novena, and Divine Mercy only.
  // Extend with more chaplets once their per-chaplet step mappings are written.
  const showVirtualBeadDrawer =
    prayerType === 'daily-rosary' ||
    prayerType === '54-day-novena' ||
    (isChaplet && chaplet === 'divine-mercy');

  const prayerSteps: PrayerStep[] = [];

  // Add intention step at the beginning (except for 54-day novena which has preset intention)
  if (!isNovena) {
    prayerSteps.push({
      id: 'set-intention',
      title: 'Set Your Intention',
      content: '',
      type: 'intention'
    });
  }

  // Handle chaplet prayers
  if (isChaplet && chaplet) {
    const chapletInfo = CHAPLET_INFO[chaplet];
    const chapletSteps = CHAPLET_PRAYERS[chaplet];

    // Add chaplet intro
    prayerSteps.push({
      id: 'chaplet-intro',
      title: chapletInfo.name,
      content: [
        `You are about to pray the ${chapletInfo.name}.`,
        chapletInfo.description,
        intention ? `Your intention: "${intention}"` : 'Take a moment to set your intention for this prayer.',
        `Estimated duration: ${chapletInfo.estimatedDuration} minutes`,
        'Begin by making the Sign of the Cross.'
      ],
      type: 'instruction'
    });

    // Add each chaplet step
    chapletSteps.forEach((step) => {
      prayerSteps.push({
        id: step.id,
        title: step.title,
        content: Array.isArray(step.content) ? step.content : [step.content],
        type: step.type as 'prayer' | 'instruction' | 'mysteries' | 'decades'
      });
    });

    // Add reflection step for chaplets
    prayerSteps.push({
      id: 'prayer-reflection',
      title: 'Prayer Reflection',
      content: '',
      type: 'reflection'
    });

    // Add completion step
    prayerSteps.push({
      id: 'chaplet-complete',
      title: 'Prayer Complete',
      content: [
        'Finish with the Sign of the Cross.',
        `Your ${chapletInfo.name} is now complete.`,
        'May God bless your faithful devotion.'
      ],
      type: 'instruction'
    });

    // Skip the rest of the rosary-specific logic
  } else if (mystery) {

  // Intro step - different for each prayer type
  if (isNovena) {
    prayerSteps.push({
      id: 'intro',
      title: `Day ${currentDay} - ${mystery} Mysteries`,
      content: [
        `Today you are praying the ${mystery} Mysteries during your ${phase} phase.`,
        intention ? `Your intention: "${intention}"` : 'Remember to hold your intention in your heart.',
        'Begin by making the Sign of the Cross.'
      ],
      type: 'instruction'
    });
  } else {
    prayerSteps.push({
      id: 'intro',
      title: `${mystery} Mysteries Rosary`,
      content: [
        `Today you are praying the ${mystery} Mysteries of the Holy Rosary.`,
        intention ? `Your intention: "${intention}"` : 'Take a moment to set your intention for this prayer.',
        'Begin by making the Sign of the Cross.'
      ],
      type: 'instruction'
    });
  }

  // Opening prayer - only for 54-day novena
  if (isNovena) {
    prayerSteps.push({
      id: 'opening-prayer',
      title: 'Opening Prayer to Mary',
      content: getOpeningPrayer(mystery, phase),
      type: 'prayer'
    });
  }

  // Rosary opening prayers
  prayerSteps.push({
    id: 'rosary-opening',
    title: 'Rosary Opening Prayers',
    content: [
      'Pray the following opening prayers:',
      '',
      '• Apostles\' Creed',
      '• 1 Our Father',
      '• 3 Hail Marys (for Faith, Hope, and Charity)',
      '• 1 Glory Be'
    ],
    type: 'instruction'
  });

  // Mysteries introduction
  prayerSteps.push({
    id: 'mysteries-intro',
    title: `The ${mystery} Mysteries`,
    content: [
      `Today you will meditate on the ${mystery} Mysteries:`,
      ...ROSARY_MYSTERIES[mystery].map((m, i) => `${i + 1}. ${m}`)
    ],
    type: 'mysteries'
  });

  // Add each decade as individual steps
  const mysteries = ROSARY_MYSTERIES[mystery];
  const reflections = MYSTERY_REFLECTIONS[mystery];
  
  mysteries.forEach((mysteryName, index) => {
    const ordinal = index === 0 ? '1st' : index === 1 ? '2nd' : index === 2 ? '3rd' : index === 3 ? '4th' : '5th';

    const content = [
      `MEDITATION:${reflections[index]}`,
      '',
      'Pray the following:',
      '• 1 Our Father',
      '• 10 Hail Marys (while meditating on this mystery)',
      '• 1 Glory Be',
      '• Fatima Prayer'
    ];

    // Add novena-specific prayers only for 54-day novena
    if (isNovena) {
      const decadePrayers = getDecadePrayers(mystery);
      content.push('', 'Then pray:', `"${decadePrayers[index]}"`);
    }
    
    // Single consolidated step for each decade
    prayerSteps.push({
      id: `decade-${index + 1}`,
      title: `${ordinal} ${mystery} Mystery: ${mysteryName}`,
      content,
      type: 'instruction'
    });
  });

  // Add traditional rosary closing prayers
  prayerSteps.push({
    id: 'hail-holy-queen',
    title: 'Hail Holy Queen',
    content: 'Hail, Holy Queen, Mother of Mercy, our life, our sweetness and our hope. To thee do we cry, poor banished children of Eve. To thee do we send up our sighs, mourning and weeping in this valley of tears. Turn then, most gracious advocate, thine eyes of mercy toward us, and after this our exile, show unto us the blessed fruit of thy womb, Jesus. O clement, O loving, O sweet Virgin Mary. Pray for us, O holy Mother of God, that we may be made worthy of the promises of Christ.',
    type: 'prayer'
  });

  prayerSteps.push({
    id: 'rosary-prayer',
    title: 'Rosary Prayer',
    content: 'O God, whose only-begotten Son, by His life, death and resurrection, has purchased for us the rewards of eternal life: grant we beseech Thee, that meditating upon these mysteries of the Most Holy Rosary of the Blessed Virgin Mary, we may imitate what they contain, and obtain what they promise, through the same Christ our Lord. Amen.',
    type: 'prayer'
  });

  // Add novena-specific closing prayer only for 54-day novena
  if (isNovena) {
    prayerSteps.push({
      id: 'closing',
      title: 'Closing Prayer',
      content: getClosingPrayer(phase, mystery, intention),
      type: 'prayer'
    });
  }

  if (!isChaplet) {
    // Add reflection step for rosary prayers (including 54-day novena)
    prayerSteps.push({
      id: 'prayer-reflection',
      title: 'Prayer Reflection',
      content: '',
      type: 'reflection'
    });

    prayerSteps.push({
      id: 'complete',
      title: 'Prayer Complete',
      content: [
        'Finish with the Sign of the Cross.',
        isNovena ? 'Your daily novena prayer is now complete.' : 'Your rosary is now complete.',
        'May God bless your faithful devotion.'
      ],
      type: 'instruction'
    });
  }

  } // Close the else if (mystery) block

  const currentStep = prayerSteps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === prayerSteps.length - 1;

  // Final medal bead index (was 62, now 60 after consolidating beads 5-7)
  const FINAL_MEDAL_INDEX = 60;

  // Handle bead click/swipe - implements multi-swipe behavior on special beads
  const handleBeadClick = (beadIndex: number, bead: BeadSequence) => {
    // Chaplets use a simpler 1:1 bead->step mapping — the rosary's multi-swipe
    // consolidation (cross, bead 5, final medal, Our Father phases) doesn't apply.
    if (prayerType === 'chaplet') {
      const stepIndex = getStepIndexForBead(beadIndex, prayerType, chaplet);
      setCurrentBeadIndex(beadIndex);
      if (stepIndex !== currentStepIndex && stepIndex < prayerSteps.length) {
        stepChangeFromBead.current = true;
        setCurrentStepIndex(stepIndex);
        if (audioEnabled) audioFeedback.stepAdvance();
      } else if (audioEnabled) {
        audioFeedback.beadTransition();
      }
      return;
    }

    const currentDecade = getDecadeForBead(currentBeadIndex);
    const newDecade = getDecadeForBead(beadIndex);
    const isMovingForward = beadIndex > currentBeadIndex;
    const isMovingBackward = beadIndex < currentBeadIndex;

    // === CROSS BEAD HANDLING (intro prayers) ===
    // The cross (bead 0) covers multiple prayer steps at the beginning:
    // - Step 0: Set intention (daily-rosary) or Intro (54-day-novena)
    // - Step 1: Intro/Sign of Cross (daily-rosary) or Opening Prayer (54-day-novena)
    // Swiping on the cross should advance through these steps before moving to bead 1
    const CROSS_BEAD_INDEX = 0;
    const onCrossBead = currentBeadIndex === CROSS_BEAD_INDEX;

    // Determine which step marks the end of cross-bead prayers (where we move to bead 1)
    // For daily-rosary: steps 0-2 stay on cross (intention, intro, rosary-opening with Apostle's Creed)
    // For 54-day-novena: steps 0-2 also stay on cross (intro, opening prayer, rosary-opening)
    // The swipe AFTER step 2 moves to bead 1 but stays on step 2 (rosary-opening)
    const CROSS_LAST_STEP = 2; // Steps 0, 1, and 2 stay on cross

    if (onCrossBead && isMovingForward) {
      // On cross bead, swiping forward
      if (currentStepIndex < CROSS_LAST_STEP) {
        // Still have intro steps to go through - advance step, stay on cross
        stepChangeFromBead.current = true;
        setCurrentStepIndex(prev => prev + 1);

        if (audioEnabled) {
          audioFeedback.stepAdvance();
        }
        return;
      } else if (currentStepIndex === CROSS_LAST_STEP) {
        // On the last cross step (rosary-opening), now move to bead 1 but STAY on same step
        // User says Apostle's Creed on cross, then moves to bead 1 for Our Father
        setCurrentBeadIndex(1);
        stepChangeFromBead.current = true;
        // Don't advance step - stay on rosary-opening (step 2)

        if (audioEnabled) {
          audioFeedback.beadTransition();
        }
        return;
      }
    }

    if (onCrossBead && isMovingBackward && currentStepIndex > 0) {
      // On cross bead, swiping backward - go to previous step
      stepChangeFromBead.current = true;
      setCurrentStepIndex(prev => prev - 1);

      if (audioEnabled) {
        audioFeedback.stepAdvance();
      }
      return;
    }

    // === BEAD 5 HANDLING (Glory Be / Mysteries / 1st Decade Our Father) ===
    // Bead 5 handles multiple steps: mysteries-intro (step 3) and decade-1 (step 4)
    // Similar to Our Father beads between decades, this needs two swipes
    const INTRO_GLORY_BE_BEAD = 5;
    const onIntroGloryBeBead = currentBeadIndex === INTRO_GLORY_BE_BEAD;

    // For daily-rosary: mysteries-intro is step 3, decade-1 is step 4
    // For 54-day-novena: mysteries-intro is step 3, decade-1 is step 4
    const mysteriesIntroStepIndex = prayerType === '54-day-novena' ? 3 : 3;
    const decade1StepIndex = mysteriesIntroStepIndex + 1;

    if (onIntroGloryBeBead && isMovingForward) {
      if (currentStepIndex === mysteriesIntroStepIndex) {
        // On bead 5, mysteries intro step - advance to 1st decade step, stay on bead 5
        stepChangeFromBead.current = true;
        setCurrentStepIndex(decade1StepIndex);

        if (audioEnabled) {
          audioFeedback.stepAdvance();
        }
        return;
      }
      // If already on decade 1 step, fall through to normal bead handling (move to bead 6)
    }

    if (onIntroGloryBeBead && isMovingBackward) {
      if (currentStepIndex === decade1StepIndex) {
        // On bead 5, 1st decade step - go back to mysteries intro, stay on bead 5
        stepChangeFromBead.current = true;
        setCurrentStepIndex(mysteriesIntroStepIndex);

        if (audioEnabled) {
          audioFeedback.stepAdvance();
        }
        return;
      }
      // If on mysteries intro step, fall through to move back to bead 4
    }

    // === FINAL MEDAL HANDLING ===
    // When on final medal, swiping forward advances through closing prayer steps
    const onFinalMedal = currentBeadIndex === FINAL_MEDAL_INDEX;
    const arrivingAtFinalMedal = isMovingForward && beadIndex === FINAL_MEDAL_INDEX && currentBeadIndex !== FINAL_MEDAL_INDEX;

    if (arrivingAtFinalMedal) {
      // First arrival at final medal - show closing prayers (Glory Be, Fatima from 5th decade)
      setCurrentBeadIndex(beadIndex);
      setOurFatherPhase('closing');

      if (audioEnabled) {
        audioFeedback.beadTransition();
      }
      return;
    }

    if (onFinalMedal && isMovingForward) {
      // On final medal, swiping forward advances prayer steps while staying on bead
      if (!isLastStep) {
        // If in closing phase, switch to opening first (represents moving past Glory Be)
        if (ourFatherPhase === 'closing') {
          setOurFatherPhase('opening');
        }

        stepChangeFromBead.current = true;
        setCurrentStepIndex(prev => Math.min(prev + 1, prayerSteps.length - 1));

        if (audioEnabled) {
          audioFeedback.stepAdvance();
        }
      }
      // Stay on final medal - don't change bead index
      return;
    }

    if (onFinalMedal && isMovingBackward) {
      // On final medal, swiping backward goes to previous step or back to 5th decade
      if (ourFatherPhase === 'opening') {
        // Go back to closing phase first
        setOurFatherPhase('closing');
        if (audioEnabled) {
          audioFeedback.beadTransition();
        }
        return;
      }
      // In closing phase, go back to 5th decade's last bead
      setCurrentBeadIndex(61); // Last Hail Mary of 5th decade
      setOurFatherPhase('opening');
      // Step will update via the normal decade logic
      if (audioEnabled) {
        audioFeedback.beadTransition();
      }
      return;
    }

    // === OUR FATHER BEAD HANDLING ===
    // Check if we're arriving at an Our Father bead from the previous decade's last Hail Mary
    const arrivingAtOurFather = isMovingForward && isOurFatherBead(beadIndex) && !isOurFatherBead(currentBeadIndex);

    // Check if we're on an Our Father bead in closing phase and swiping forward
    const onOurFatherClosing = isOurFatherBead(currentBeadIndex) && ourFatherPhase === 'closing' && isMovingForward;

    // Check if we're moving backward from an Our Father bead in opening phase
    const leavingOurFatherBackward = isOurFatherBead(currentBeadIndex) && ourFatherPhase === 'opening' && isMovingBackward;

    if (arrivingAtOurFather) {
      // First swipe to Our Father bead: show closing prayers (Glory Be, Fatima)
      setCurrentBeadIndex(beadIndex);
      setOurFatherPhase('closing');

      // Don't change the step yet - stay on current decade step
      if (audioEnabled) {
        audioFeedback.beadTransition();
      }
    } else if (onOurFatherClosing && beadIndex === currentBeadIndex + 1) {
      // Second swipe on Our Father bead: transition to opening phase and change step
      // Note: beadIndex might be +1 (next small bead) but we stay on Our Father
      setOurFatherPhase('opening');

      // Now change to the next decade step
      const stepIndex = getStepIndexForBead(currentBeadIndex, prayerType, chaplet);
      if (stepIndex !== currentStepIndex && stepIndex < prayerSteps.length) {
        stepChangeFromBead.current = true;
        setCurrentStepIndex(stepIndex);

        if (audioEnabled) {
          audioFeedback.decadeComplete();
        }
      }
      // Don't advance bead index - stay on Our Father for the opening prayers
      return;
    } else if (leavingOurFatherBackward) {
      // Moving backward from Our Father opening phase: go back to closing phase first
      setOurFatherPhase('closing');
      if (audioEnabled) {
        audioFeedback.beadTransition();
      }
      // Don't change bead index - stay on Our Father
      return;
    } else {
      // Normal bead transition (within Hail Marys or other beads)
      setCurrentBeadIndex(beadIndex);

      // Reset phase to opening when leaving Our Father bead forward
      if (isOurFatherBead(currentBeadIndex) && !isOurFatherBead(beadIndex)) {
        setOurFatherPhase('opening');
      }

      // Check if we're crossing decade boundaries (for non-Our Father transitions)
      const shouldChangeStep =
        (currentDecade?.decade !== newDecade?.decade) ||
        (currentDecade === null && newDecade !== null) ||
        (currentDecade !== null && newDecade === null);

      if (shouldChangeStep) {
        const stepIndex = getStepIndexForBead(beadIndex, prayerType, chaplet);

        if (stepIndex !== currentStepIndex && stepIndex < prayerSteps.length) {
          stepChangeFromBead.current = true;
          setCurrentStepIndex(stepIndex);

          if (audioEnabled) {
            audioFeedback.decadeComplete();
          }
        }
      } else {
        if (audioEnabled) {
          audioFeedback.beadTransition();
        }
      }
    }
  };

  const nextStep = () => {
    if (!isLastStep) {
      setCurrentStepIndex(prev => {
        const newIndex = prev + 1;
        
        // Trigger audio feedback based on step type
        if (audioEnabled) {
          const nextStepData = prayerSteps[newIndex];
          
          if (nextStepData?.type === 'mysteries') {
            audioFeedback.mysteryTransition();
          } else if (nextStepData?.type === 'decades') {
            audioFeedback.decadeComplete();
          } else if (newIndex === prayerSteps.length - 1) {
            audioFeedback.prayerComplete();
          } else {
            audioFeedback.stepAdvance();
          }
        }
        
        return newIndex;
      });
      // Bead index will be updated automatically by useEffect
    }
  };

  const prevStep = () => {
    if (!isFirstStep) {
      setCurrentStepIndex(prev => prev - 1);
      // Bead index will be updated automatically by useEffect
    }
  };

  // Handle intention submission
  const handleIntentionSubmit = (data: { intention?: string; mood?: MoodType }) => {
    setJournalData(prev => ({
      ...prev,
      intention: data.intention,
      mood: data.mood
    }));
    nextStep(); // Move to next prayer step
  };

  // Handle intention skip
  const handleIntentionSkip = () => {
    nextStep(); // Move to next prayer step without capturing intention
  };

  // Handle reflection submission
  const handleReflectionSubmit = (data: {
    reflection?: string;
    mood?: MoodType;
    gratitudes?: string[];
    insights?: string;
    tags?: string[];
  }) => {
    const finalJournalData = {
      ...journalData,
      ...data
    };
    setJournalData(finalJournalData);

    // Clear saved progress when prayer is completed
    localStorage.removeItem(prayerSessionKey);

    releaseWakeLock();
    onComplete(finalJournalData);
    onClose();
    setCurrentStepIndex(0);
    setCurrentBeadIndex(0);
  };

  // Handle reflection skip
  const handleReflectionSkip = () => {
    // Clear saved progress when prayer is completed
    localStorage.removeItem(prayerSessionKey);

    releaseWakeLock();
    onComplete(journalData);
    onClose();
    setCurrentStepIndex(0);
    setCurrentBeadIndex(0);
  };

  const handleComplete = () => {
    // This should only be called for the final completion step
    // Journal data should already be collected by now
    localStorage.removeItem(prayerSessionKey);

    releaseWakeLock();
    onComplete(journalData);
    onClose();
    setCurrentStepIndex(0);
    setCurrentBeadIndex(0);
  };

  const handleResetProgress = () => {
    // Clear saved progress and restart from beginning
    localStorage.removeItem(prayerSessionKey);
    setCurrentStepIndex(0);
    setCurrentBeadIndex(0);
  };

  // Long-press on Previous button to reset progress
  const handlePrevPointerDown = () => {
    longPressTimer.current = setTimeout(() => {
      handleResetProgress();
      setShowResetToast(true);
      setTimeout(() => setShowResetToast(false), 2000);
      longPressTimer.current = null;
    }, 800);
  };

  const handlePrevPointerUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const renderContent = (content: string | string[], type: string) => {
    if (Array.isArray(content)) {
      return (
        <div className="space-y-3">
          {content.map((line, index) => {
            // Check if this line mentions a prayer name that we have expandable content for
            if (line.startsWith('•') && type === 'instruction') {
              const prayerName = line.replace('•', '').trim();
              const prayerText = getPrayerText(prayerName);
              
              if (prayerText) {
                return (
                  <div key={index} className="ml-4">
                    <ExpandablePrayer
                      prayerName={prayerName}
                      prayerText={prayerText}
                      className="mb-2"
                    />
                  </div>
                );
              }
            }
            
            // Handle meditation text with special formatting
            if (line.startsWith('MEDITATION:')) {
              const meditationText = line.replace('MEDITATION:', '').trim();
              return (
                <p key={index} className="text-center italic text-gray-700 dark:text-gray-300 my-4 leading-relaxed">
                  {meditationText}
                </p>
              );
            }

            return (
              <p key={index} className={`
                ${type === 'instruction' ? 'text-gray-700 dark:text-gray-300' : 'text-gray-800 dark:text-gray-200'}
                ${line.startsWith('•') ? 'ml-4' : ''}
                ${line === '' ? 'h-2' : ''}
              `}>
                {line}
              </p>
            );
          })}
        </div>
      );
    }

    // Handle single prayer content - check if it's a known prayer (but exclude opening prayer)
    if (type === 'prayer') {
      const prayerText = content as string;
      
      // Skip expandable for opening prayer (contains 54-day novena specific content)
      if (prayerText.includes('Crown of Roses') || prayerText.includes('dispenser of God\'s graces') || prayerText.includes('Queen of the Most Holy Rosary')) {
        // This is the 54-day novena opening prayer - don't make it expandable
      }
      // Check for traditional prayers that should be expandable
      else if (prayerText.includes('Hail, Holy Queen') && prayerText.includes('Mother of Mercy')) {
        return (
          <ExpandablePrayer
            prayerName="Hail Holy Queen"
            prayerText={prayerText}
          />
        );
      }
      else if (prayerText.includes('only-begotten Son') || prayerText.includes('Most Holy Rosary')) {
        return (
          <ExpandablePrayer
            prayerName="Rosary Prayer"
            prayerText={prayerText}
          />
        );
      }
    }

    return (
      <p className={`
        ${type === 'prayer' ? 'font-medium leading-relaxed text-gray-800 dark:text-gray-200' : 'text-gray-700 dark:text-gray-300'}
        ${type === 'prayer' ? 'italic' : ''}
      `}>
        {content}
      </p>
    );
  };

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 transition-all duration-300 ${showRosaryDrawer ? 'pr-16' : ''}`}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[95vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-600 transition-colors duration-300">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mr-4">{currentStep.title}</h2>
          <div className="flex items-center gap-2 flex-shrink-0">
            {showVirtualBeadDrawer && (
              <RosaryToggleButton
                onClick={() => setShowRosaryDrawer(!showRosaryDrawer)}
                isOpen={showRosaryDrawer}
                side="right"
              />
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-2">
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 transition-colors duration-300">
            <div 
              className="bg-indigo-600 dark:bg-indigo-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStepIndex + 1) / prayerSteps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentStep.type === 'intention' ? (
            <PrePrayerIntention
              onSubmit={handleIntentionSubmit}
              onSkip={handleIntentionSkip}
              initialIntention={journalData.intention}
              initialMood={journalData.mood}
            />
          ) : currentStep.type === 'reflection' ? (
            <PostPrayerReflection
              onSubmit={handleReflectionSubmit}
              onSkip={handleReflectionSkip}
              initialData={journalData}
            />
          ) : (
            <div className={`
              ${currentStep.type === 'prayer' ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-500 pl-6 py-4 transition-colors duration-300' : ''}
            `}>
              {renderContent(currentStep.content, currentStep.type)}
            </div>
          )}

        </div>

        {/* Footer - Hide for journal steps as they have their own buttons */}
        {currentStep.type !== 'intention' && currentStep.type !== 'reflection' && (
          <div className="border-t border-gray-200 dark:border-gray-600 px-6 py-3 transition-colors duration-300">
            {showRosaryDrawer && (prayerType === 'daily-rosary' || prayerType === '54-day-novena') && currentStep.id && currentStep.id !== 'set-intention' && currentStep.id !== 'intro' && currentStep.id !== 'opening-prayer' && (
              <RosaryFooterCounter
                currentBeadIndex={currentBeadIndex}
                currentStepIndex={currentStepIndex}
                totalSteps={prayerSteps.length}
                ourFatherPhase={ourFatherPhase}
                prayerType={prayerType}
                className="mb-2"
              />
            )}
            <div className="flex justify-between">
              <button
                onClick={prevStep}
                disabled={isFirstStep}
                onPointerDown={!isFirstStep ? handlePrevPointerDown : undefined}
                onPointerUp={handlePrevPointerUp}
                onPointerLeave={handlePrevPointerUp}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg transition-colors select-none
                  ${isFirstStep
                    ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  } transition-colors duration-300
                `}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              {isLastStep ? (
                <button
                  onClick={handleComplete}
                  className="bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 text-white px-8 py-2 rounded-lg font-semibold transition-colors duration-300"
                >
                  Amen 🙏
                </button>
              ) : (
                <button
                  onClick={nextStep}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors duration-300"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Reset toast */}
        {showResetToast && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 text-sm px-4 py-2 rounded-full shadow-lg animate-fade-in">
            Progress reset
          </div>
        )}
      </div>

      {/* Virtual Rosary Drawer — rosary + 54-day novena + divine-mercy pilot */}
      {showVirtualBeadDrawer && (
        <VirtualRosaryDrawer
          isOpen={showRosaryDrawer}
          onClose={() => setShowRosaryDrawer(false)}
          side="right"
          prayerType={isChaplet ? (chaplet as string) : prayerType}
          currentBeadIndex={currentBeadIndex}
          onBeadClick={handleBeadClick}
        />
      )}
    </div>
  );
};